<template>
  <div class="entity-table-container">
    <div v-if="isEmpty" class="empty-state p-4 border rounded text-center">
      <p class="mb-3">No {{ entityName.toLowerCase() }} exist yet for your company.</p>
      <a :href="createUrl" class="btn btn-primary">
        <i class="fas fa-plus mr-05"></i> Add {{ entityName }}
      </a>
    </div>
    
    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h3>{{ entityName }} List</h3>
        <a :href="createUrl" class="btn btn-sm btn-primary">
          <i class="fas fa-plus mr-05"></i> Add {{ entityName }}
        </a>
      </div>
      
      <div class="table-responsive table-entry-listing">
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <!-- Conditionally render meta header columns if metaHeaders is not empty -->
              <template v-if="metaHeaders && metaHeaders.length">
                <th v-for="metaKey in metaHeaders" :key="metaKey">
                  {{ metaKey }}
                </th>
              </template>
              <th>Created</th>
              <th>Updated</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id" :class="maybeDeactivated(entry)">
              <td>
                <a :href="entry.link">
                  {{ entry.display_attributes.name || 'Unnamed ' + entityName }}
                </a>
                {{ entry.deactivated ? '- (deactivated)' : '' }}
              </td>
              
              <!-- Additional table cells for each display_meta property if it exists -->
              <template v-if="entry.display_meta">
                <td v-for="(metaValue, metaKey) in entry.display_meta" :key="metaKey">
                  <slot
                    name="meta-cell"
                    :entry="entry"
                    :meta-key="metaKey"
                    :meta-value="metaValue"
                    :formatted-value="formatMetaValue(metaValue)"
                  >
                    <span class="badge badge-secondary">
                      {{ formatMetaValue(metaValue) }}
                    </span>
                  </slot>
                </td>
              </template>
              
              <td>{{ entry.created_at }}</td>
              <td>{{ entry.updated_at }}</td>
              <td class="text-right">
                <a :href="entry.link" class="btn btn-sm btn-info mr-1" title="View/Edit" v-tooltip="'View/Edit'">
                  <i class="fas fa-edit"></i>
                </a>
                <a
                  @click="toggleActivation(entry)"
                  :class="entry.deactivated ? 'btn btn-sm btn-success mr-1' : 'btn btn-sm btn-danger mr-1'"
                  :title="entry.deactivated ? 'Activate' : 'Deactivate'"
                  v-tooltip="entry.deactivated ? 'Activate' : 'Deactivate'">
                  <i :class="entry.deactivated ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                </a>
                <button
                  v-if="isAccredited && isFacility"
                  class="btn btn-sm btn-primary download-certificate-btn"
                  :data-facility-id="entry.id"
                  title="Download Certificate"
                  v-tooltip="'Download Certificate'">
                  <i class="fas fa-download"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    entityName: {
      type: String,
      required: true
    },
    companyId: {
      type: Number,
      required: true
    },
    entries: {
      type: Array,
      required: true,
      default: () => []
    },
    createUrl: {
      type: String,
      required: true
    },
    editUrl: {
      type: String,
      required: true
    },
    isAccredited: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    formatMetaValue(metaValue) {
      const labelMap = {
        'physician': 'Supervising Physician',
        'tech': 'Remote Technologist',
        'ctaide': 'CT Aide',
        'mriaide': 'MRI Aide',
        'medical_director': 'Medical Director',
      };

      if (Array.isArray(metaValue)) {
        return metaValue.map(v => labelMap[v] || v).join(', ');
      }
      return labelMap[metaValue] || metaValue;
    },
    maybeDeactivated(entry) {
      return entry.deactivated ? '-deactivated' : '';
    },
    toggleActivation(entry) {
      // Confirm with the user before toggling activation status
      const action = entry.deactivated ? 'activate' : 'deactivate';
      const confirmMessage = `Are you sure you want to ${action} this ${this.entityName.toLowerCase()}?`;
      
      if (!confirm(confirmMessage)) {
        return;
      }
      
      // Call the API to toggle activation status
      $.ajax({
        url: localized.resturl + '/company/toggle-activation',
        method: 'POST',
        beforeSend: function(xhr) {
          xhr.setRequestHeader('X-WP-Nonce', localized.restnonce);
        },
        dataType: 'json',
        data: {
          entity_id: entry.id,
          action: action // 'activate' or 'deactivate'
        },
        success: (result) => {
          if (result.success) {
            
            // Show success message
            toastr.success(
              `${this.entityName} ${action}d successfully`, 
              'Success'
            );
            location.reload(); // Reload the page to reflect changes
          } else {
            // Show error message
            toastr.error(
              result.message || `Failed to ${action} ${this.entityName.toLowerCase()}`, 
              'Error'
            );
          }
        },
        error: (xhr, status, error) => {
          // Show error message
          toastr.error(
            `Failed to ${action} ${this.entityName.toLowerCase()}: ${error}`, 
            'Error'
          );
        }
      });
    },
  },
  
  computed: {
    metaHeaders() {
      // Loop through entries and return the keys from the first entry that has a display_meta property.
      for (let entry of this.entries) {
        if (entry.display_meta && Object.keys(entry.display_meta).length > 0) {
          return Object.keys(entry.display_meta);
        }
      }
      return [];
    },
    isEmpty() {
      return this.entries.length === 0;
    },
    isFacility() {
      return this.entityName.toLowerCase() === 'facility';
    }
  }
}
</script>